/* Reya core — reveal animations, wishlist, toast, modal, prefetch.
   All strings come from window.ReyaSettings (output from theme settings in layout/theme.liquid). */
(() => {
  const RS = (window.ReyaSettings = window.ReyaSettings || {});
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------- Reveal on scroll ---------------- */
  const revealIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-in');
              revealIO.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -6% 0px', threshold: 0 }
        )
      : null;

  const initReveal = (root = document) => {
    root.querySelectorAll('[data-rr-reveal], .rr-draw').forEach((el) => {
      if (el.classList.contains('is-in')) return;
      if (el.classList.contains('rr-draw')) {
        el.querySelectorAll('path').forEach((p) => {
          try {
            p.style.setProperty('--rr-path-len', Math.ceil(p.getTotalLength()));
          } catch (e) {}
        });
      }
      if (!revealIO || reduceMotion) {
        el.classList.add('is-in');
        return;
      }
      // Anything already on screen at load stays visible (fast LCP); only content below the fold animates in.
      const rect = el.getBoundingClientRect();
      const onScreen = rect.top < window.innerHeight && rect.bottom > 0 && !el.closest('[hidden]');
      if (onScreen && !el.classList.contains('rr-pre')) {
        el.classList.add('is-in');
        return;
      }
      el.classList.add('rr-pre');
      revealIO.observe(el);
    });
  };

  /* ---------------- Lazy videos ---------------- */
  const videoIO =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              const v = entry.target;
              if (entry.isIntersecting) {
                if (!v.dataset.rrLoaded) {
                  v.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
                  v.load();
                  v.dataset.rrLoaded = '1';
                }
                if (v.hasAttribute('data-rr-autoplay') && !reduceMotion) v.play().catch(() => {});
              } else if (!v.paused) {
                v.pause();
              }
            });
          },
          { rootMargin: '200px 0px' }
        )
      : null;

  const initVideos = (root = document) => {
    root.querySelectorAll('video[data-rr-lazy]').forEach((v) => {
      if (videoIO) videoIO.observe(v);
      else v.querySelectorAll('source[data-src]').forEach((s) => (s.src = s.dataset.src));
    });
  };

  /* ---------------- Toast ---------------- */
  let toastTimer;
  const TOAST_ICONS = {
    heart: '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="currentColor" d="M12 21s-7.5-4.6-9.6-9.2C.9 8.4 3 4.5 6.7 4.5c2.1 0 3.6 1.2 4.3 2.4.7-1.2 2.2-2.4 4.3-2.4 3.7 0 5.8 3.9 4.3 7.3C19.5 16.4 12 21 12 21z"/></svg>',
    'heart-off': '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path fill="none" stroke="currentColor" stroke-width="1.6" d="M12 20s-7-4.3-9-8.6C1.6 8.2 3.6 5 6.9 5c2 0 3.4 1.1 4.1 2.3.7-1.2 2.1-2.3 4.1-2.3 3.3 0 5.3 3.2 3.9 6.4C17 15.7 12 20 12 20z"/></svg>',
  };
  const toast = (message, link, opts = {}) => {
    let el = document.querySelector('.rr-toast');
    if (!el) {
      el = document.createElement('div');
      el.className = 'rr-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      document.body.appendChild(el);
    }
    el.innerHTML = '';
    el.classList.toggle('rr-toast--icon', !!(opts.icon && TOAST_ICONS[opts.icon]));
    if (opts.icon && TOAST_ICONS[opts.icon]) {
      const ic = document.createElement('span');
      ic.className = 'rr-toast__icon';
      ic.innerHTML = TOAST_ICONS[opts.icon];
      el.appendChild(ic);
    }
    const span = document.createElement('span');
    span.className = 'rr-toast__msg';
    span.textContent = message;
    el.appendChild(span);
    if (link && link.url) {
      const a = document.createElement('a');
      a.href = link.url;
      a.textContent = link.label;
      el.appendChild(a);
    }
    requestAnimationFrame(() => el.classList.add('is-visible'));
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => el.classList.remove('is-visible'), 3200);
  };

  /* ---------------- Wishlist ---------------- */
  const KEY = 'rr:wishlist';
  const Wishlist = {
    get() {
      try {
        return JSON.parse(localStorage.getItem(KEY)) || [];
      } catch (e) {
        return [];
      }
    },
    set(list) {
      try {
        localStorage.setItem(KEY, JSON.stringify(list));
      } catch (e) {}
      document.dispatchEvent(new CustomEvent('rr:wishlist:change', { detail: { list } }));
    },
    has(handle) {
      return this.get().includes(handle);
    },
    toggle(handle) {
      const list = this.get();
      const i = list.indexOf(handle);
      if (i > -1) list.splice(i, 1);
      else list.unshift(handle);
      this.set(list);
      return i === -1;
    },
    remove(handle) {
      this.set(this.get().filter((h) => h !== handle));
    },
    clear() {
      this.set([]);
    },
  };
  window.ReyaWishlist = Wishlist;

  const syncWishlistUI = () => {
    const list = Wishlist.get();
    document.querySelectorAll('[data-rr-wishlist-count]').forEach((el) => {
      el.textContent = list.length ? list.length : '';
      el.dataset.count = list.length;
    });
    document.querySelectorAll('rr-wishlist-button').forEach((el) => el.sync && el.sync());
  };

  if (!customElements.get('rr-wishlist-button')) {
    customElements.define(
      'rr-wishlist-button',
      class extends HTMLElement {
        connectedCallback() {
          this.button = this.querySelector('button');
          if (!this.button) return;
          this.button.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            const added = Wishlist.toggle(this.dataset.handle);
            this.button.classList.remove('is-pop');
            void this.button.offsetWidth;
            this.button.classList.add('is-pop');
            toast(added ? RS.wishlistAdded : RS.wishlistRemoved, added ? { url: RS.wishlistUrl, label: RS.wishlistView } : null, { icon: added ? 'heart' : 'heart-off' });
          });
          this.sync();
        }
        sync() {
          if (!this.button) return;
          const on = Wishlist.has(this.dataset.handle);
          this.button.setAttribute('aria-pressed', on ? 'true' : 'false');
          const label = this.button.querySelector('[data-rr-wish-label]');
          if (label) label.textContent = on ? this.dataset.labelRemove : this.dataset.labelAdd;
        }
      }
    );
  }
  document.addEventListener('rr:wishlist:change', syncWishlistUI);
  window.addEventListener('storage', (e) => e.key === KEY && syncWishlistUI());

  /* ---------------- Modal helper ([data-rr-modal-open="id"]) ---------------- */
  let lastFocus;
  const openModal = (id) => {
    const m = document.getElementById(id);
    if (!m) return;
    // A modal rendered inside quick view must sit on the page itself, or the quick view clips/hides it.
    if (m.parentElement !== document.body && m.closest('quick-add-modal')) {
      document.querySelectorAll('body > .rr-modal[data-rr-moved]').forEach((old) => old.id === id && old.remove());
      m.dataset.rrMoved = '';
      document.body.appendChild(m);
    }
    lastFocus = document.activeElement;
    m.classList.add('is-open');
    m.setAttribute('aria-hidden', 'false');
    document.documentElement.classList.add('rr-lock');
    const f = m.querySelector('button, [href], input, [tabindex]:not([tabindex="-1"])');
    f && setTimeout(() => f.focus(), 50);
  };
  const closeModal = (m) => {
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.classList.remove('rr-lock');
    lastFocus && lastFocus.focus && lastFocus.focus();
  };
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-rr-modal-open]');
    if (opener) {
      e.preventDefault();
      openModal(opener.dataset.rrModalOpen);
      return;
    }
    const closer = e.target.closest('[data-rr-modal-close]');
    if (closer) closeModal(closer.closest('.rr-modal'));
  });
  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Escape') return;
    const open = document.querySelector('.rr-modal.is-open');
    open && closeModal(open);
  });
  window.ReyaModal = { open: openModal, close: closeModal };

  /* ---------------- Scroller arrows ([data-rr-scroller] + [data-rr-prev]/[data-rr-next]) ---------------- */
  const initScrollers = (root = document) => {
    root.querySelectorAll('[data-rr-scroller-wrap]').forEach((wrap) => {
      if (wrap.dataset.rrInit) return;
      wrap.dataset.rrInit = '1';
      const track = wrap.querySelector('[data-rr-scroller]');
      const prev = wrap.querySelector('[data-rr-prev]');
      const next = wrap.querySelector('[data-rr-next]');
      if (!track) return;
      const step = () => {
        const item = track.firstElementChild;
        return item ? item.getBoundingClientRect().width + parseFloat(getComputedStyle(track).columnGap || 16) : track.clientWidth;
      };
      const update = () => {
        const max = track.scrollWidth - track.clientWidth - 2;
        prev && (prev.disabled = track.scrollLeft <= 2);
        next && (next.disabled = track.scrollLeft >= max);
      };
      prev && prev.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: 'smooth' }));
      next && next.addEventListener('click', () => track.scrollBy({ left: step(), behavior: 'smooth' }));
      track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
      update();
    });
  };

  /* ---------------- Instant prefetch on hover ---------------- */
  const initPrefetch = () => {
    if (!RS.prefetch) return;
    const conn = navigator.connection;
    if (conn && (conn.saveData || /2g/.test(conn.effectiveType || ''))) return;
    const done = new Set();
    const prefetch = (url) => {
      if (done.has(url)) return;
      done.add(url);
      const l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = url;
      document.head.appendChild(l);
    };
    const handler = (e) => {
      const a = e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      let url;
      try {
        url = new URL(a.href);
      } catch (err) {
        return;
      }
      if (url.origin !== location.origin || url.hash || /\/(cart|checkout|account)/.test(url.pathname)) return;
      if (url.pathname === location.pathname && url.search === location.search) return;
      prefetch(url.href);
    };
    document.addEventListener('mouseover', handler, { passive: true });
    document.addEventListener('touchstart', handler, { passive: true });
  };

  /* ---------------- WebMCP: expose store actions to in-browser AI agents (progressive enhancement) ---------------- */
  const initAgentTools = () => {
    const mc = navigator.modelContext;
    if (!mc || typeof mc.registerTool !== 'function') return;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const json = (r) => (r.ok ? r.json() : Promise.reject(new Error('HTTP ' + r.status)));
    const safe = (fn) => async (input) => {
      try {
        return { content: [{ type: 'text', text: JSON.stringify(await fn(input || {})) }] };
      } catch (e) {
        return { content: [{ type: 'text', text: 'Error: ' + e.message }], isError: true };
      }
    };
    const tools = [
      {
        name: 'search_products',
        description: 'Search the Reya catalogue (silver charms, charm bracelets, pendants, gifts). Returns products with title, url, price and availability.',
        inputSchema: { type: 'object', properties: { query: { type: 'string', description: 'What to search for' }, limit: { type: 'number' } }, required: ['query'] },
        execute: safe(({ query, limit }) =>
          fetch(`${root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,collection&resources[limit]=${Math.min(limit || 8, 10)}`)
            .then(json)
            .then((d) => ({
              products: (d.resources.results.products || []).map((p) => ({ title: p.title, url: p.url, price: p.price, available: p.available, handle: p.handle })),
              collections: (d.resources.results.collections || []).map((c) => ({ title: c.title, url: c.url })),
            }))
        ),
      },
      {
        name: 'get_product',
        description: 'Get full details of a product by its handle, including variants and their ids for add_to_cart.',
        inputSchema: { type: 'object', properties: { handle: { type: 'string' } }, required: ['handle'] },
        execute: safe(({ handle }) =>
          fetch(`${root}products/${encodeURIComponent(handle)}.js`).then(json).then((p) => ({
            title: p.title,
            url: p.url,
            description: (p.description || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim(),
            variants: p.variants.map((v) => ({ id: v.id, title: v.title, price: v.price / 100, available: v.available })),
          }))
        ),
      },
      {
        name: 'add_to_cart',
        description: 'Add a product variant to the shopping bag.',
        inputSchema: { type: 'object', properties: { variant_id: { type: 'number' }, quantity: { type: 'number' } }, required: ['variant_id'] },
        execute: safe(({ variant_id, quantity }) =>
          fetch(`${root}cart/add.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: variant_id, quantity: quantity || 1 }] }),
          })
            .then(json)
            .then((d) => {
              document.dispatchEvent(new CustomEvent('rr:cart:updated'));
              return { added: d.items.map((i) => ({ title: i.title, quantity: i.quantity })), cart_url: `${root}cart` };
            })
        ),
      },
      {
        name: 'get_cart',
        description: 'Read the current shopping bag contents and total.',
        inputSchema: { type: 'object', properties: {} },
        execute: safe(() =>
          fetch(`${root}cart.js`).then(json).then((c) => ({
            item_count: c.item_count,
            total: c.total_price / 100,
            currency: c.currency,
            items: c.items.map((i) => ({ title: i.title, quantity: i.quantity, price: i.final_line_price / 100, url: i.url })),
            checkout_url: `${root}checkout`,
          }))
        ),
      },
      {
        name: 'get_wishlist',
        description: 'List the product handles the shopper has saved to their wishlist on this device.',
        inputSchema: { type: 'object', properties: {} },
        execute: safe(() => ({ handles: Wishlist.get(), url: RS.wishlistUrl })),
      },
    ];
    tools.forEach((t) => {
      try {
        mc.registerTool(t);
      } catch (e) {}
    });
  };

  /* ---------------- Deferred scripts: <script data-rr-defer-src="…"> load after page load or on first interaction ---------------- */
  let deferredDone = false;
  const loadDeferredScripts = () => {
    if (deferredDone) return;
    deferredDone = true;
    document.querySelectorAll('script[data-rr-defer-src]').forEach((stub) => {
      const s = document.createElement('script');
      s.src = stub.dataset.rrDeferSrc;
      s.async = false;
      document.head.appendChild(s);
    });
  };
  const initDeferredScripts = () => {
    ['pointerdown', 'keydown', 'touchstart', 'scroll', 'focusin'].forEach((ev) =>
      window.addEventListener(ev, loadDeferredScripts, { once: true, passive: true, capture: true })
    );
    const later = () => ('requestIdleCallback' in window ? requestIdleCallback(loadDeferredScripts, { timeout: 2500 }) : setTimeout(loadDeferredScripts, 1500));
    if (document.readyState === 'complete') later();
    else window.addEventListener('load', later, { once: true });
  };

  /* ---------------- Boot ---------------- */
  const boot = (root) => {
    initReveal(root);
    initVideos(root);
    initScrollers(root);
  };
  window.Reya = { toast, initReveal, initVideos, initScrollers, boot, wishlist: Wishlist };

  const start = () => {
    boot(document);
    syncWishlistUI();
    initPrefetch();
    initAgentTools();
    initDeferredScripts();
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();

  document.addEventListener('shopify:section:load', (e) => {
    boot(e.target);
    syncWishlistUI();
  });
})();
